'use client';

import React, { useEffect, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUp } from 'lucide-react';
import { MegaFooter } from '@/components/ui/home/mega-footer';
import { useFooter } from '@/contexts/FooterContext';
import { useTenant } from '@/contexts/TenantContext';
import { About } from '@/types';
import type { LandingPage } from '@/types/landingPage';
import { getActiveTemplate } from '@/templates';

type ConditionalFooterProps = {
  contactData: LandingPage | null;
  aboutData: About | null;
};

function scrollRoot(): HTMLElement | null {
  return (
    document.querySelector('.collabs-scroll')
    || document.querySelector('.fixedlayout')
  ) as HTMLElement | null;
}

function footerSlot(): HTMLElement | null {
  return (
    document.querySelector('.collabs-scroll')
    || document.querySelector('[data-footer-slot]')
  ) as HTMLElement | null;
}

function subscribeToScrollRoot(onChange: () => void) {
  const observer = new MutationObserver(onChange);
  observer.observe(document.body, { childList: true, subtree: true });
  return () => observer.disconnect();
}

const serverScrollRoot = () => null;

function UpdatesBar() {
  const { footerMode, setIsFooterOpen } = useFooter();
  const onClick = () => {
    if (footerMode === 'toggle') {
      setIsFooterOpen(false);
      return;
    }
    scrollRoot()?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="bg-black text-white px-4 py-3" data-footer-anchor>
      <button type="button" onClick={onClick} className="nav-button inline-flex items-center gap-1">
        <span>{footerMode === 'toggle' ? 'SCROLL UP' : 'SEE RECENT UPDATES'}</span>
        <ArrowUp size={16} />
      </button>
    </div>
  );
}

export function ConditionalFooter({ contactData, aboutData }: ConditionalFooterProps) {
  const { designId } = useTenant();
  const { footerMode, isFooterOpen, setIsFooterOpen } = useFooter();
  const slot = useSyncExternalStore(subscribeToScrollRoot, footerSlot, serverScrollRoot);

  useEffect(() => {
    if (footerMode !== 'toggle' || !isFooterOpen) return undefined;
    const close = () => setIsFooterOpen(false);
    window.addEventListener('wheel', close, { passive: true });
    return () => window.removeEventListener('wheel', close);
  }, [footerMode, isFooterOpen, setIsFooterOpen]);

  if (getActiveTemplate(designId).Shell || footerMode === 'hidden' || !contactData || !aboutData) return null;

  if (footerMode === 'toggle') {
    return (
      <div
        className="fixed inset-0 z-30 pointer-events-none"
        style={{
          transform: isFooterOpen ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div className="h-full overflow-y-auto pointer-events-auto">
          <UpdatesBar />
          <MegaFooter contactData={contactData} aboutData={aboutData} />
        </div>
      </div>
    );
  }

  if (!slot) return null;

  return createPortal(
    <>
      <UpdatesBar />
      <MegaFooter contactData={contactData} aboutData={aboutData} />
    </>,
    slot,
  );
}
